import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, signal, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { Utils } from '../../../shared/utils';
import { IMPORTS } from './_imports';

@Component({
    selector: 'pdf-upload-multiple',
    templateUrl: './pdf-upload-multiple.component.html',
    styleUrl: './pdf-upload-multiple.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class PdfUploadMultipleComponent implements OnChanges {
    // #region [ViewChild]
    @ViewChild('anchorFileRef') anchorFileRef: ElementRef;
    // #endregion

    // #region [const]
    private MAX_SIZE_MB: number = 15;
    // #endregion

    // #region [signals]
    $disabled = signal(false);
    $value = signal(null as any);
    $fileNames = signal([] as string[]);
    // #endregion

    // #region [Input/Output]
    @Input('maxFiles') _maxFiles: number;
    @Input('disabled') _disabled: boolean;
    @Input('value') _value: any;
    @Output('valueChange') _valueChange = new EventEmitter<any>();
    // #endregion

    constructor(private toastr: ToastrService) { }

    ngOnChanges() {
        this.$disabled.set(this._disabled);
        this.$value.set(this._value);
    }

    changeFile(event) {
        const file = event.target.files[0] as File;

        // caso a extensão do arquivo não seja PDF
        if (file.name.split('.').slice(-1)[0].toLowerCase() != 'pdf') {
            // reinicia valores do input e do componente Formio
            this.removeFile(event);
            this.toastr.error(Enums.Messages.PdfFilesOnly, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        // caso o tamanho do arquivo seja maior que o máximo estabelecido
        if (file.size > this.MAX_SIZE_MB * 1024 * 1024) {
            // reinicia valores do input e do componente Formio
            this.removeFile(event);
            this.toastr.error(Enums.Messages.PdfMaxSizeLimit.replace('{0}', this.MAX_SIZE_MB.toString()), Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        let fileReader = new FileReader();
        fileReader.readAsArrayBuffer(file);
        fileReader.onloadend = () => {
            // converte o arquivo para um array de bytes, uma vez que o "JSON.stringify > JSON.parse"
            // que ocorre no meio do caminho até o hook do evento de submit do Formio
            // transformaria o objeto do tipo File em um objeto vazio ("{}")
            let byteArray = [];
            let array = new Uint8Array(fileReader.result as ArrayBuffer);
            for (let byte of array) {
                byteArray.push(byte);
            }

            Utils.updSignal(this.$fileNames, x => {
                x.push(file.name);
                return x;
            });

            Utils.updSignal(this.$value, x => {
                x.fileName.push(file.name);
                x.fileType.push(file.type);
                x.fileContent.push(byteArray);
                return x;
            });

            this._valueChange.emit(this.$value());

            this.anchorFileRef.nativeElement.value = '';
        };
    }

    removeFile(idx: number) {
        Utils.updSignal(this.$fileNames, x => {
            x.splice(idx, 1);
            return x;
        });

        Utils.updSignal(this.$value, x => {
            x.fileName.splice(idx, 1);
            x.fileType.splice(idx, 1);
            x.fileContent.splice(idx, 1);
            return x;
        });

        this._valueChange.emit(this.$value());
    }

    candAddMoreFiles(): boolean {
        return (
            // caso o componente não tenha terminado de carregar ainda
            this.$value() == null
            || (
                // caso o componente esteja em modo de edição (/flow-definition)
                this.$value()?.minioKey == null
                // limite de arquivos definido no Construtor de Formulário
                && this.$value()?.fileName?.length < this._maxFiles
            )
            // caso não haja nenhum arquivo inserido ainda
            || this.$value()?.fileName?.length == 0
        );
    }

    // #region [===== private methods =====]
    // #endregion
}
